/**
 * ChatHeaderV2 — the persistent V2 chat header (bead terra-design-v2-chat-yub.3).
 *
 * Mounted once per view by `FlowChatPanelV2.tsx` — the SAME component in
 * both the session-list landing view and the per-session drill-in, so the
 * title never scrolls away and the two views are structurally identical
 * (`h-12`, `border-b-[0.5px] border-border-primary`, breadcrumb left /
 * actions right). Only the second crumb and the action cluster's contents
 * vary between the two states, driven by the `state` discriminant below —
 * see this file's own "Goal" in the bead for the acceptance criterion.
 *
 * This component owns no data hooks (coding-standards.md: "V2 components
 * are markup + local UI state only") — every value it reads comes from
 * `useFlowChatListController`/`useFlowChatSessionController`
 * (`EnrollQueue/useFlowChatControllers.ts`), threaded down as props by
 * `FlowChatPanelV2.tsx`. It owns exactly one piece of local UI state: which
 * header dropdown (if any) is open in THREAD state — the conversation-
 * history crumb's dropdown, or the in-session "new chat" picker — mirroring
 * V1's own `openHeaderDropdown` tri-state (`EnrollQueue/FlowChatPanel.tsx`),
 * which coding-standards.md / `useFlowChatControllers.ts`'s doc comment both
 * call out as deliberately NOT shared/controller-owned (unlike the LIST
 * view's "new chat" picker open-state, which IS shared with V1 via the
 * controller's own `newSessionPickerOpen` — both markup trees need the same
 * fact there).
 *
 * Crumb 1 ("Chats"): the list state's OWN current page (no `onClick`,
 * renders inert per `Breadcrumb`'s "last crumb, no onClick" branch) in list
 * state; a clickable link back to the list (`onBack`) in thread state. This
 * REPLACES V1's old back-to-the-session-list button AND its in-thread
 * all-sessions peek dropdown (brief §3 — Spencer review round 2;
 * `FlowChatPanel.tsx`'s `allChatsOpen`/`FlowChatSessionList` peek has no V2
 * equivalent).
 *
 * Crumb 2 (thread only): `${accountLabel ?? 'No account'}: ${chatTitle}` —
 * its accessible name (computed from `Breadcrumb`'s own text-content
 * child, no separate `aria-label` needed) is byte-identical to V1's
 * explicit `aria-label` on the same control (`FlowChatPanel.tsx:537`).
 * Clicking it toggles `ChatConversationDropdown` — the SAME component V1
 * uses, reused as-is (coding-standards.md / this bead's "Compose With":
 * never fork its `useKeystoneConversations`/`useFlowChatSessionList` merge).
 * `Breadcrumb` itself (bead .13) is consumed, never forked — the dropdown's
 * open/close is tracked in THIS file's own local state and positioned in a
 * sibling `<div>`, not inside `Breadcrumb`. The one prop this header sets is
 * `size="sm"`: Glaze `Tab size="sm"`'s `text-sm font-semibold`, so the crumbs
 * read as the same header as `Header.tsx`'s Companies/People/Plays/Signals
 * tabs in the same 48px band (Spencer QA 2026-10-07); colour, truncation and
 * the separator are the shared component's own.
 *
 * "New chat" is a single LABELLED `Button size="sm" variant="secondary"` in
 * BOTH states (V1 used a labelled button in list state but a bare "+"
 * `IconButton` in thread state — this bead's spec explicitly asks for the
 * label in both). Behaviour mirrors V1 exactly: a page account underneath
 * the dock means a direct start-or-resume, no popover; no page account
 * means a Glaze `Popover` (list: `GlobalSearch mode="accounts"` directly;
 * thread: "Start new chat with {this session's account}" plus the same
 * `GlobalSearch` picker for a different one, V1's two-section panel).
 *
 * Copy-link and Close are thread/both respectively — see the bead's "What
 * to build" list. Copy-link renders ONLY in thread state (list state has no
 * conversation to copy a link to).
 */
import { useCallback, useRef, useState } from 'react';
import { Button } from '@clay-run/glaze/button';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { Popover, PopoverPanel, PopoverTrigger } from '@clay-run/glaze/popover';
import { FLOW_ICONS } from '../../icons';
import { Tab, TabList, Tabs } from '@clay-run/glaze/tabs';
import { Breadcrumb } from '../ui/Breadcrumb';
import { GlobalSearch } from '../../../search/GlobalSearch';
import { ChatConversationDropdown } from '../../EnrollQueue/ChatConversationDropdown';
import { useClickOutside } from '../../useClickOutside';

export type ChatHeaderV2Props =
  | {
      state: 'list';
      /** The page account underneath the dock, or null — see
       *  `useFlowChatControllers.ts`'s `FlowChatListController.pageAccountId`
       *  doc comment. */
      pageAccountId: string | null;
      /** Shared with V1 via the list controller — see this file's own doc
       *  comment for why list state's picker-open fact is NOT local here. */
      newSessionPickerOpen: boolean;
      onNewChatClick: () => void;
      onPickNewSessionAccount: (accountId: string) => void;
      onCloseNewSessionPicker: () => void;
      onClose: () => void;
    }
  | {
      state: 'thread';
      accountId: string;
      accountLabel: string | undefined;
      chatTitle: string;
      conversationId: string | null;
      sessionId: string;
      pageAccountId: string | null;
      onBack: () => void;
      onSelectConversation: (conversationId: string) => void;
      onSelectDraftSession: (sessionId: string) => void;
      onStartNewForAccount: () => void;
      onStartNewForDifferentAccount: (accountId: string) => void;
      /** Page-account direct-start only (controller's own no-op-without-a-
       *  page-account contract) — see `FlowChatSessionController`'s doc
       *  comment. The "else open the dropdown" fallback is THIS file's own
       *  local UI state, same as V1's. */
      onInSessionNewChatClick: () => void;
      onCopyLink: () => void;
      onClose: () => void;
    };

/** Which THREAD-state header dropdown (if any) is open — mutually
 *  exclusive, mirroring V1's `openHeaderDropdown`. Not used in list state:
 *  list state's only dropdown (the new-chat picker) uses the shared
 *  controller fact instead. */
type ThreadDropdown = 'conversation' | 'newChat' | null;

export function ChatHeaderV2(props: ChatHeaderV2Props) {
  const [openDropdown, setOpenDropdown] = useState<ThreadDropdown>(null);
  // Stable identity: `useClickOutside` lists `onClose` in its effect's
  // dependency array, so a bare arrow recreated every render would tear
  // down and re-push this header's outside-click layer on every re-render
  // while the dropdown is open — and this header re-renders on every stream
  // tick. Only the topmost layer evaluates a click, so churn here can
  // re-order a nested picker opened over this dropdown beneath it mid-run.
  const closeDropdown = useCallback(() => setOpenDropdown(null), []);

  const crumbRegionRef = useRef<HTMLDivElement>(null);
  const conversationPanelRef = useClickOutside(openDropdown === 'conversation', closeDropdown, crumbRegionRef);

  const crumbs =
    props.state === 'thread'
      ? [
          { label: 'Chats', onClick: props.onBack },
          {
            label: `${props.accountLabel ?? 'No account'}: ${props.chatTitle}`,
            onClick: () => setOpenDropdown((prev) => (prev === 'conversation' ? null : 'conversation')),
          },
        ]
      : [];

  return (
    <div
      data-testid="chat-header-v2"
      className="flex h-12 shrink-0 items-center justify-between gap-2 border-b-[0.5px] border-border-primary px-3"
    >
      <div ref={crumbRegionRef} className={props.state === 'list' ? 'min-w-0 flex-1 self-stretch' : 'relative min-w-0 flex-1'}>
        {props.state === 'list' ? (
          // Same `Tabs` grammar as the dock's tab row (`rail/RailDock.tsx`), so
          // the list landing's strip reads as the dock's sibling.
          <Tabs value="chats" onValueChange={() => {}} size="sm" layout="fit" className="h-full min-w-0">
            <TabList className="h-full">
              <Tab value="chats">Chats</Tab>
            </TabList>
          </Tabs>
        ) : (
          <Breadcrumb crumbs={crumbs} size="sm" />
        )}
        {props.state === 'thread' && openDropdown === 'conversation' && (
          <div ref={conversationPanelRef} className="absolute left-0 top-full z-40 mt-1 w-72">
            <ChatConversationDropdown
              accountId={props.accountId}
              accountName={props.accountLabel}
              currentConversationId={props.conversationId}
              currentSessionId={props.sessionId}
              onSelect={(id) => {
                props.onSelectConversation(id);
                closeDropdown();
              }}
              onSelectDraftSession={(id) => {
                props.onSelectDraftSession(id);
                closeDropdown();
              }}
              onNew={() => {
                props.onStartNewForAccount();
                closeDropdown();
              }}
            />
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {props.state === 'list' ? (
          <ListNewChatControl
            pageAccountId={props.pageAccountId}
            open={props.newSessionPickerOpen}
            onTriggerClick={props.onNewChatClick}
            onOpenChange={(next) => {
              if (!next) props.onCloseNewSessionPicker();
            }}
            onPick={props.onPickNewSessionAccount}
          />
        ) : (
          <ThreadNewChatControl
            pageAccountId={props.pageAccountId}
            accountLabel={props.accountLabel}
            open={openDropdown === 'newChat'}
            onDirectStart={props.onInSessionNewChatClick}
            onOpenChange={(next) => setOpenDropdown(next ? 'newChat' : null)}
            onStartForAccount={() => {
              props.onStartNewForAccount();
              closeDropdown();
            }}
            onStartForDifferentAccount={(accountId) => {
              props.onStartNewForDifferentAccount(accountId);
              closeDropdown();
            }}
          />
        )}

        {props.state === 'thread' && (
          <IconButton
            icon={FLOW_ICONS.copyLink}
            variant="ghost"
            size="sm"
            aria-label="Copy chat link"
            disabled={!props.conversationId}
            title={props.conversationId ? undefined : 'Send a message first'}
            onClick={props.onCopyLink}
          />
        )}

        <IconButton
          icon={FLOW_ICONS.close}
          variant="ghost"
          size="sm"
          aria-label="Close sidebar"
          onClick={props.onClose}
        />
      </div>
    </div>
  );
}

/** List state's "New chat" — a page account means a direct start-or-resume
 *  (`onTriggerClick` alone covers both branches: the shared controller's
 *  `handleNewChatClick` already contains the pageAccountId check, so no
 *  second check belongs here); no page account opens the shared-state
 *  `GlobalSearch mode="accounts"` popover (V1 `FlowChatPanel.tsx:157-195`). */
function ListNewChatControl({
  pageAccountId,
  open,
  onTriggerClick,
  onOpenChange,
  onPick,
}: {
  pageAccountId: string | null;
  open: boolean;
  onTriggerClick: () => void;
  onOpenChange: (next: boolean) => void;
  onPick: (accountId: string) => void;
}) {
  if (pageAccountId) {
    return (
      <Button size="sm" variant="secondary" onClick={onTriggerClick}>
        New chat
      </Button>
    );
  }
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger
        render={
          <Button size="sm" variant="secondary" aria-haspopup="listbox" aria-expanded={open} onClick={onTriggerClick}>
            New chat
          </Button>
        }
      />
      {/* Glaze's `PopoverPanel` defaults to `overflow-y-auto overscroll-none`
          whenever `showArrow` is false (our default here) — per the CSS spec,
          a non-visible value on one overflow axis forces the other axis to
          compute as `auto` too, so this ~288px popup clips BOTH axes. The
          account-search dropdown below is its own `absolute`, `w-[380px]`
          overlay (`GlobalSearch.tsx`) that manages its own scrolling/clipping
          — it needs none from this ancestor — so `!overflow-visible` (an
          `!important` override, required to win: Glaze merges classes with
          plain `clsx`, not `tailwind-merge`, so two same-specificity
          utility classes are not deduped and the winner is whichever lands
          later in Tailwind's generated stylesheet, not later in this string)
          restores the popup to its un-clipped default instead of silently
          hiding the dropdown (bead terra-design-v2-chat-yub bug: "New chat"
          account search rendered nothing at all). */}
      <PopoverPanel positioner={{ align: 'end' }} className="w-72 [--popover-padding:0px] !overflow-visible">
        <GlobalSearch mode="accounts" activeEntity="account" onSelectAccount={onPick} />
      </PopoverPanel>
    </Popover>
  );
}

/** Thread state's "New chat" — a page account means a direct start-or-
 *  resume of THAT account (`onDirectStart`, the controller's own page-
 *  account-only primitive; the popover is never shown). No page account
 *  opens a two-section panel: "Start new chat with {this session's
 *  account}" plus the same different-account `GlobalSearch` picker V1 uses
 *  (`FlowChatPanel.tsx:579-592`). */
function ThreadNewChatControl({
  pageAccountId,
  accountLabel,
  open,
  onDirectStart,
  onOpenChange,
  onStartForAccount,
  onStartForDifferentAccount,
}: {
  pageAccountId: string | null;
  accountLabel: string | undefined;
  open: boolean;
  onDirectStart: () => void;
  onOpenChange: (next: boolean) => void;
  onStartForAccount: () => void;
  onStartForDifferentAccount: (accountId: string) => void;
}) {
  const isOpen = pageAccountId ? false : open;
  return (
    <Popover open={isOpen} onOpenChange={pageAccountId ? undefined : onOpenChange}>
      <PopoverTrigger
        render={
          <Button
            size="sm"
            variant="secondary"
            aria-haspopup={pageAccountId ? undefined : 'listbox'}
            aria-expanded={pageAccountId ? undefined : isOpen}
            onClick={pageAccountId ? onDirectStart : undefined}
          >
            New chat
          </Button>
        }
      />
      {/* Same `!overflow-visible` override as `ListNewChatControl` above, and
          for the same reason — this panel's own "different account" section
          nests the identical `GlobalSearch mode="accounts"` dropdown, which
          Glaze's default `overflow-y-auto overscroll-none` clips to near-
          nothing before it ever reaches this panel's ancestors. See that
          sibling call site's comment for the full root cause and why
          `!important` (not just class order) is required to win here. */}
      <PopoverPanel positioner={{ align: 'end' }} className="w-72 [--popover-padding:0px] !overflow-visible">
        <button
          type="button"
          onClick={onStartForAccount}
          className="w-full rounded-t-lg border-b border-border-secondary px-3 py-2 text-left text-[13px] font-medium text-content-action hover:bg-bg-secondary"
        >
          Start new chat with {accountLabel ?? 'this account'}
        </button>
        <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-content-tertiary">
          Start new chat with a different account
        </div>
        <div className="px-3 pb-3">
          <GlobalSearch mode="accounts" activeEntity="account" onSelectAccount={onStartForDifferentAccount} />
        </div>
      </PopoverPanel>
    </Popover>
  );
}
